import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Pencil, Send } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError } from '@/shared/api/client';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import { Switch } from '@/shared/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Textarea } from '@/shared/ui/textarea';
import { useEmailMarketing, useEmailMarketingPaths } from '@/features/email-marketing';
import { usePlatform } from '../model/demo-context';
import { DEFAULT_SURVEY_BLOCK } from '../model/survey-block';
import { fillVariables, VARIABLE_SAMPLES, type LocalizedText, type SurveyBlockConfig, type SystemEmail } from '../model/types';
import { LocalizedFields, VariableChips } from './platform-fields';

const LEGACY_HTML = '__legacy__';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** E-mails que o sistema manda sozinho. A linha abre o editor; o switch liga o envio. */
export function SystemEmailsTab() {
  const { config, saveEmail } = usePlatform();
  const { templates } = useEmailMarketing();
  const [editing, setEditing] = useState<SystemEmail | null>(null);
  const templateName = (id: string | null) => (id ? templates.find((t) => t.id === id)?.name ?? 'Template removido' : 'HTML do legado');
  const variantCount = (id: string | null) => (id ? templates.filter((t) => t.variantOf === id).length : 0);

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>E-mail</TableHead>
                <TableHead className="hidden md:table-cell">Assunto</TableHead>
                <TableHead>Template</TableHead>
                <TableHead className="text-right">Ativo</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {config.emails.map((email) => {
                const variants = variantCount(email.templateId);
                return (
                  <TableRow key={email.kind} className="cursor-pointer" onClick={() => setEditing(email)} tabIndex={0}>
                    <TableCell>
                      <p className="text-sm font-medium">{email.label}</p>
                      <p className="text-xs text-muted-foreground">{email.trigger}</p>
                    </TableCell>
                    <TableCell className="hidden max-w-[20rem] truncate font-mono text-xs md:table-cell">{email.subject.pt}</TableCell>
                    <TableCell className="text-sm">
                      {email.templateId ? templateName(email.templateId) : <span className="text-muted-foreground">HTML do legado</span>}
                      {variants > 0 ? (
                        <p className="text-xs text-muted-foreground">
                          + {variants} variante{variants > 1 ? 's' : ''} por loja
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Switch checked={email.enabled} onCheckedChange={(enabled) => saveEmail(email.kind, { enabled })} aria-label={`${email.label} ativo`} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <ChevronRight className="size-4" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <SystemEmailSheet email={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function SystemEmailSheet({ email, onClose }: { email: SystemEmail | null; onClose: () => void }) {
  const navigate = useNavigate();
  const paths = useEmailMarketingPaths();
  const { templates } = useEmailMarketing();
  const { saveEmail, sendTestEmail } = usePlatform();
  const [subject, setSubject] = useState<LocalizedText>({ pt: '', es: '', en: '' });
  const [templateId, setTemplateId] = useState<string>(LEGACY_HTML);
  const [enabled, setEnabled] = useState(true);
  const [surveyBlock, setSurveyBlock] = useState<SurveyBlockConfig | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (!email) return;
    setSubject(email.subject);
    setTemplateId(email.templateId ?? LEGACY_HTML);
    setEnabled(email.enabled);
    setSurveyBlock(email.kind === 'transaction' ? { ...DEFAULT_SURVEY_BLOCK, ...email.surveyBlock } : null);
  }, [email]);

  if (!email) return <Sheet open={false} />;

  // Só os padrões entram aqui; as variantes por loja ficam ligadas ao padrão pelo `variantOf`.
  const defaults = templates.filter((template) => !template.appliesTo?.length);
  const variants = templates.filter((template) => template.variantOf === templateId);

  const save = () => {
    saveEmail(email.kind, {
      subject,
      templateId: templateId === LEGACY_HTML ? null : templateId,
      enabled,
      ...(surveyBlock ? { surveyBlock } : {}),
    });
    toast.success(`${email.label} salvo.`);
    onClose();
  };

  const sendTest = async () => {
    const to = testEmail.trim();
    if (!EMAIL_RE.test(to)) {
      toast.error('Informe um e-mail válido.');
      return;
    }
    setSendingTest(true);
    try {
      await sendTestEmail(email.kind, to);
      toast.success(`Teste enviado para ${to}.`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        toast.error('O servidor ainda não tem o envio de teste.', {
          description: `Falta a rota POST /v1/platform/emails/${email.kind}/test no fixe-api. O contrato está em docs/plataforma-integracao.md.`,
        });
      } else {
        toast.error(error instanceof ApiError ? error.message : 'Não foi possível enviar o teste.');
      }
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:w-[36rem] sm:max-w-[36rem]">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            {email.label}
            <Badge variant={enabled ? 'success' : 'secondary'}>{enabled ? 'Ativo' : 'Desligado'}</Badge>
          </SheetTitle>
          <p className="text-sm text-muted-foreground">{email.trigger}</p>
        </SheetHeader>

        <div className="space-y-5">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2">
            <Label htmlFor={`enabled-${email.kind}`} className="font-normal">
              Enviar este e-mail
            </Label>
            <Switch id={`enabled-${email.kind}`} checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <LocalizedFields id={`subject-${email.kind}`} label="Assunto" value={subject} onChange={setSubject} />
          <p className="-mt-3 text-xs text-muted-foreground">Prévia: {fillVariables(subject.pt, VARIABLE_SAMPLES)}</p>

          <div className="space-y-1.5">
            <Label>Template padrão</Label>
            <div className="flex gap-2">
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="min-w-0 flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LEGACY_HTML}>HTML do legado (como está hoje)</SelectItem>
                  {defaults.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templateId !== LEGACY_HTML ? (
                <Button type="button" variant="secondary" onClick={() => navigate(paths.templateEdit(templateId))}>
                  <Pencil />
                  Editar
                </Button>
              ) : null}
            </div>
            {variants.length > 0 ? (
              <ul className="space-y-1 text-xs text-muted-foreground">
                {variants.map((variant) => (
                  <li key={variant.id}>
                    <button type="button" className="underline-offset-4 hover:underline" onClick={() => navigate(paths.templateEdit(variant.id))}>
                      {variant.name}
                    </button>{' '}
                    · {variant.appliesTo?.map((store) => store.name).join(', ')}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">
                Vale para todas as lojas. Para mudar só em algumas, edite o template e escolha as lojas em “Aplicar em”.
              </p>
            )}
          </div>

          {surveyBlock ? <SurveyBlockFields kind={email.kind} value={surveyBlock} onChange={setSurveyBlock} /> : null}

          <div className="space-y-1.5 rounded-xl border border-border/60 p-3">
            <Label htmlFor={`teste-${email.kind}`}>Enviar teste</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id={`teste-${email.kind}`}
                type="email"
                inputMode="email"
                className="min-w-0 flex-1"
                placeholder="voce@fixecrm.com.br"
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void sendTest();
                  }
                }}
              />
              <Button type="button" variant="secondary" onClick={() => void sendTest()} loading={sendingTest} disabled={!testEmail.trim()}>
                <Send /> Enviar teste
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Usa o que está salvo, com as variáveis de exemplo. Salve antes de testar uma alteração.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Variáveis</Label>
            <VariableChips variables={email.variables} />
          </div>

          <p className="text-xs text-muted-foreground">Legado: {email.legacy}</p>

          <div className="flex justify-end">
            <Button type="button" onClick={save}>
              Salvar
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Como o `{{bloco_pesquisa}}` é montado: texto com link ou banner clicável. */
function SurveyBlockFields({
  kind,
  value,
  onChange,
}: {
  kind: string;
  value: SurveyBlockConfig;
  onChange: (next: SurveyBlockConfig) => void;
}) {
  const set = (patch: Partial<SurveyBlockConfig>) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-3 rounded-xl border border-border/60 p-3">
      <div>
        <p className="text-sm font-medium">Bloco de pesquisa</p>
        <p className="text-xs text-muted-foreground">
          Só nas lojas com pesquisa transacional. Entra onde o template tiver <code className="font-mono">{'{{bloco_pesquisa}}'}</code>;
          mova a variável no editor para mudar a ordem.
        </p>
      </div>
      <RadioGroup
        className="flex flex-wrap gap-4"
        value={value.format}
        onValueChange={(format) => set({ format: format as SurveyBlockConfig['format'] })}
      >
        <label htmlFor={`pesquisa-texto-${kind}`} className="flex items-center gap-2 text-sm">
          <RadioGroupItem value="text" id={`pesquisa-texto-${kind}`} /> Texto com link
        </label>
        <label htmlFor={`pesquisa-banner-${kind}`} className="flex items-center gap-2 text-sm">
          <RadioGroupItem value="banner" id={`pesquisa-banner-${kind}`} /> Banner com link
        </label>
      </RadioGroup>
      {value.format === 'banner' ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`pesquisa-url-${kind}`}>URL da imagem</Label>
            <Input
              id={`pesquisa-url-${kind}`}
              value={value.bannerUrl}
              onChange={(event) => set({ bannerUrl: event.target.value })}
              placeholder="https://…/banner.png"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`pesquisa-alt-${kind}`}>Texto alternativo</Label>
            <Input id={`pesquisa-alt-${kind}`} value={value.bannerAlt} onChange={(event) => set({ bannerAlt: event.target.value })} />
          </div>
          {value.bannerUrl.trim() ? (
            <img src={value.bannerUrl.trim()} alt={value.bannerAlt} className="max-h-40 w-full rounded-lg bg-muted/30 object-contain" />
          ) : (
            <p className="text-xs text-muted-foreground">Sem imagem, o e-mail sai com o texto e o link.</p>
          )}
        </>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`pesquisa-msg-${kind}`}>Texto</Label>
            <Textarea id={`pesquisa-msg-${kind}`} rows={2} value={value.message} onChange={(event) => set({ message: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`pesquisa-link-${kind}`}>Texto do link</Label>
            <Input id={`pesquisa-link-${kind}`} value={value.linkLabel} onChange={(event) => set({ linkLabel: event.target.value })} />
          </div>
          <p className="rounded-lg bg-muted/30 px-3 py-2 text-center text-sm">
            {value.message}
            <span className="text-[#127DB3] underline">{value.linkLabel}</span>
          </p>
        </>
      )}
    </div>
  );
}
